import React, { useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Select } from 'src/components/Select';
import { Input } from 'src/components/Input';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  AdminPlatformUser,
  USER_STATUSES,
  formatUserDisplayName,
  formatUserStatusLabel,
  updatePlatformUserStatus,
} from 'src/api/adminUsers';

type UserStatusModalProps = {
  user: AdminPlatformUser;
  onClose: () => void;
  onUpdated: () => void;
};

const UserStatusModal: React.FC<UserStatusModalProps> = ({ user, onClose, onUpdated }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(user.status ?? 'ACTIVE');
  const [saving, setSaving] = useState(false);

  const firstName = user.name?.first ?? '';
  const lastName = user.name?.last ?? '';
  const phone =
    user.phone_number?.code || user.phone_number?.number
      ? `${user.phone_number?.code ?? ''} ${user.phone_number?.number ?? ''}`.trim()
      : '';

  const handleSave = async () => {
    if (!user.id || status === user.status) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await updatePlatformUserStatus(user.id, status);
      dispatch(setAppAlert({ message: 'User status updated', isSuccess: true }));
      onUpdated();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to update user status',
          isSuccess: false,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      footer={
        <Container display="flex" gap="0.75rem" justifyContent="flex-end" width="100%">
          <Button text="Cancel" className="outline" onClick={onClose} />
          <Button
            text={saving ? 'Saving...' : 'Save'}
            className="black"
            disabled={saving || status === user.status}
            onClick={handleSave}
          />
        </Container>
      }
    >
      <Container fontWeight={600} fontSize="1.25rem" paddingBottom="0.35rem">
        Edit user
      </Container>
      <Container fontSize="0.8125rem" paddingBottom="1rem">
        Only status can be saved for now. Other fields are placeholders until the extended edit API
        is available.
      </Container>

      <Input label="First name" value={firstName} disabled readonly />
      <Input label="Last name" value={lastName} disabled readonly />
      <Input label="Email" value={user.email ?? ''} disabled readonly />
      <Input label="Phone" value={phone} disabled readonly />
      <Input label="Country" value={(user.country_code as string) || ''} disabled readonly />

      <Select
        label="Status"
        value={status}
        options={USER_STATUSES.map((value) => ({
          value,
          label: formatUserStatusLabel(value),
        }))}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Container fontSize="0.75rem" paddingTop="0.25rem">
        Editing {formatUserDisplayName(user)}
      </Container>
    </Modal>
  );
};

export default UserStatusModal;
